import React, { useMemo, useState } from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { isEmailRowActionAllowed } from './reportEmailActionEligibility';

const buildLabel = (action) => action?.params?.label || action?.type || 'Action';

const getRowValue = (row, field) => {
  if (!row || !field) return undefined;
  const key = String(field);
  if (Object.prototype.hasOwnProperty.call(row, key)) return row[key];
  if (!key.includes('.')) return row[key];
  return key
    .split('.')
    .map((segment) => segment.trim())
    .filter(Boolean)
    .reduce((acc, segment) => (acc == null ? acc : acc[segment]), row);
};

const buildRequiredFields = (action) => {
  if (Array.isArray(action?.params?.requiredFields) && action.params.requiredFields.length) {
    return action.params.requiredFields;
  }
  if (action?.type === 'execute_trigger') {
    const bacField = action?.params?.bacField || 'bac';
    const programField = action?.params?.programField || 'programName';
    const statusField = action?.params?.statusField || 'newStatus';
    return [bacField, programField, statusField];
  }
  if (action?.type === 'open_detail_panel') {
    const bacField = action?.params?.bacField || 'bac';
    const programField = action?.params?.programField || 'programName';
    return [bacField, programField];
  }
  return [];
};

const ReportRowActionMenu = ({ row, actions, onAction }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const resolvedActions = useMemo(
    () =>
      (actions || []).map((action) => {
        const requiredFields = buildRequiredFields(action);
        const fieldsPresent = requiredFields.every((field) => {
          const value = getRowValue(row, field);
          return value !== undefined && value !== null && String(value).trim() !== '';
        });
        const protectedGenericAction =
          (action?.type === 'email_send_retry' &&
            row?.genericActions?.retry === false) ||
          (action?.type === 'email_send_delete' &&
            row?.genericActions?.delete === false);
        const enabled = fieldsPresent && !protectedGenericAction &&
          isEmailRowActionAllowed(row, action?.type);
        return { action, enabled };
      }),
    [actions, row]
  );

  const visibleActions = useMemo(
    () => resolvedActions.filter(({ enabled }) => enabled),
    [resolvedActions]
  );

  if (!visibleActions.length) return null;

  const handleClose = () => setAnchorEl(null);
  const handleClick = (action) => {
    handleClose();
    onAction(action, row);
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label="Row actions"
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <MoreVertIcon fontSize="inherit" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {visibleActions.map(({ action }, index) => (
          <MenuItem key={`${action.type}-${index}`} onClick={() => handleClick(action)}>
            {buildLabel(action)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ReportRowActionMenu;
